import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Users, Activity } from 'lucide-react';
import TicketTrendsChart from './TicketTrendsChart';
import { useTicketKpis } from '../../hooks/useTicketAnalytics';

const AnalyticsPanel: React.FC = () => {
  const { kpis } = useTicketKpis();
  
  const demoKpis = {
    totalTickets: 1247,
    activeUsers: 892,
    resolutionTimeHours: 2.4,
    csatPct: 94,
    deltas: {
      totalTickets: 12,
      activeUsers: 8,
      resolutionTimeHours: -15,
      csatPct: 3
    }
  };
  
  const displayKpis = kpis || demoKpis;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 px-8"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent flex items-center">
          <BarChart3 className="w-6 h-6 mr-3 text-[#4267A1]" />
          Analytics and Insights
        </h2>
        <button className="bg-gradient-to-r from-[#8A9150] to-[#9BA55F] hover:from-[#7A8140] hover:to-[#8A9150] text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 font-medium shadow-lg">
          <TrendingUp className="w-4 h-4" />
          Generate Report
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { 
            label: 'Total Tickets', 
            value: displayKpis.totalTickets.toLocaleString(), 
            change: `${displayKpis.deltas?.totalTickets > 0 ? '+' : ''}${displayKpis.deltas?.totalTickets || 0}%`, 
            icon: Activity 
          },
          { 
            label: 'Active Users', 
            value: displayKpis.activeUsers.toLocaleString(), 
            change: `${displayKpis.deltas?.activeUsers > 0 ? '+' : ''}${displayKpis.deltas?.activeUsers || 0}%`, 
            icon: Users 
          },
          { 
            label: 'Resolution Time', 
            value: `${displayKpis.resolutionTimeHours.toFixed(1)}h`, 
            change: `${displayKpis.deltas?.resolutionTimeHours > 0 ? '+' : ''}${displayKpis.deltas?.resolutionTimeHours || 0}%`, 
            icon: TrendingUp 
          },
          { 
            label: 'Satisfaction', 
            value: `${displayKpis.csatPct.toFixed(0)}%`, 
            change: `${displayKpis.deltas?.csatPct > 0 ? '+' : ''}${displayKpis.deltas?.csatPct || 0}%`, 
            icon: BarChart3 
          }
        ].map((metric, index) => (
          <motion.div
            key={index}
            className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 border border-gray-200 shadow-lg"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <metric.icon className="w-5 h-5 text-[#4267A1]" />
              <span className="text-sm text-[#8A9150] font-medium">{metric.change}</span>
            </div>
            <div className="text-2xl font-bold text-gray-800 mb-1">{metric.value}</div>
            <div className="text-sm text-gray-600">{metric.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Ticket Trends</h3>
          <TicketTrendsChart />
        </div>
        
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Metrics</h3>
          <div className="space-y-4">
            {[
              { label: 'First Response Time', value: '15 min', target: '< 30 min' },
              { label: 'Resolution Rate', value: '87%', target: '> 85%' },
              { label: 'Customer Satisfaction', value: '4.6/5', target: '> 4.0' }
            ].map((metric, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-gray-700">{metric.label}</span>
                <div className="text-right">
                  <div className="text-gray-800 font-semibold">{metric.value}</div>
                  <div className="text-xs text-gray-500">Target: {metric.target}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AnalyticsPanel;
